import { useState, useEffect } from 'react';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, UserPlus, AlertTriangle } from 'lucide-react';
import { useGameParticipants } from '@/hooks/useGameManagement';
import { useTeamPlayers } from '@/hooks/useTeams';
import { useGameRealTimeUpdates } from '@/hooks/useGameRealTimeUpdates';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import { TeamLogo } from './TeamLogo';
import { EnhancedCountdownTimer } from './EnhancedCountdownTimer';
import { useGameCountdown } from '@/hooks/useGameManagement';
import { supabase } from '@/integrations/supabase/client';
import type { GameWithDetails, GameParticipant } from '@/types/gameSystem';

interface GameRosterManagementProps {
  game: GameWithDetails;
  currentUserId: string;
}

export const GameRosterManagement = ({ game, currentUserId }: GameRosterManagementProps) => {
  const [selectedTeamSide, setSelectedTeamSide] = useState<'home' | 'away'>('home');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [playerNames, setPlayerNames] = useState<Record<string, string>>({});

  // Check if this is a venue game or regular game to use appropriate data structure
  const isVenueGame = !(game as any).participants; // venue games don't have participants loaded
  
  const { participants, addParticipant, updateParticipantStatus, removeParticipant, loading } = useGameParticipants(game.id);
  const countdown = useGameCountdown(game.countdown_end_at);
  
  // Add real-time updates for roster changes
  useGameRealTimeUpdates({
    gameId: game.id,
    userId: currentUserId,
    onRosterUpdate: () => {
      // Participants will automatically refresh via the hook
    },
    onPlayerStatusUpdate: () => {
      // Status updates will automatically refresh
    }
  });
  
  // Get team players based on selected team side
  const teamId = selectedTeamSide === 'home' ? game.home_team_id : game.away_team_id;
  const { players } = useTeamPlayers(teamId || undefined);

  const getParticipantsByTeamSide = (teamSide: 'home' | 'away') => {
    return participants.filter(p => p.team_side === teamSide);
  };

  const getReadyPlayerCount = (teamSide: 'home' | 'away') => {
    return getParticipantsByTeamSide(teamSide).filter(p => 
      ['confirmed', 'ready_to_play'].includes(p.participation_status)
    ).length;
  };

  const handleAddPlayer = async () => {
    if (!selectedPlayerId || !teamId) return;

    const success = await addParticipant({
      player_id: selectedPlayerId,
      player_type: 'supabase',
      team_id: teamId,
      team_side: selectedTeamSide,
      participation_status: 'requested',
      eligibility_status: 'eligible',
      requested_by: currentUserId
    });

    if (success) {
      setSelectedPlayerId('');
    }
  };

  const handleStatusUpdate = async (participantId: string, status: GameParticipant['participation_status']) => {
    await updateParticipantStatus(participantId, {
      participation_status: status
    });
  };

  const getAvailablePlayers = () => {
    const currentParticipantIds = participants
      .filter(p => p.team_side === selectedTeamSide)
      .map(p => p.player_id);
    
    console.log('🎯 Available players debug:', {
      allPlayers: players,
      currentParticipants: currentParticipantIds,
      selectedTeamSide,
      teamId,
      gameHomeTeamId: game.home_team_id,
      gameAwayTeamId: game.away_team_id,
      playersLength: players.length
    });
    
    if (!teamId) {
      console.log('❌ No teamId, cannot load players');
      return [];
    }
    
    return players.filter(player => !currentParticipantIds.includes(player.user_id));
  };

  // Load player names when participants change
  useEffect(() => {
    const loadPlayerNames = async () => {
      const names: Record<string, string> = {};
      
      for (const participant of participants) {
        if (!names[participant.player_id]) {
          try {
            // First try Supabase profiles
            const { data: profile } = await supabase
              .from('profiles')
              .select('username, first_name, last_name')
              .eq('id', participant.player_id)
              .single();
              
            if (profile) {
              names[participant.player_id] = profile.username || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Player';
            } else {
              // Then try internal users
              const { data: internalUser } = await supabase
                .from('internal_users')
                .select('username')
                .eq('id', participant.player_id)
                .single();
                
              names[participant.player_id] = internalUser?.username || 'Unknown Player';
            }
          } catch (error) {
            console.error('Error fetching player name:', error);
            names[participant.player_id] = 'Unknown Player';
          }
        }
      }
      
      setPlayerNames(names);
    };

    if (participants.length > 0) {
      loadPlayerNames();
    }
  }, [participants]);

  const getPlayerName = (playerId: string) => {
    return playerNames[playerId] || 'Loading...';
  };

  const statusOptions: GameParticipant['participation_status'][] = [
    'requested', 'confirmed', 'ready_to_play', 'no_show', 'injured', 'substituted', 'joining'
  ];

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Game Roster Management - {game.game_name}
        </DialogTitle>
      </DialogHeader>
          {/* Game Status and Countdown */}
          {game.countdown_end_at && countdown && (
            <EnhancedCountdownTimer
              countdown={countdown}
              variant="detailed"
              className="bg-card"
            />
          )}

          {/* Team Selection */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <label className="text-sm font-medium">Manage Team:</label>
            <Select value={selectedTeamSide} onValueChange={(value) => setSelectedTeamSide(value as 'home' | 'away')}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">
                  <div className="flex items-center gap-2">
                    <TeamLogo 
                      teamName={game.home_team?.team_name || game.home_team_label}
                      logoUrl={game.home_team?.team_logo}
                      size="sm"
                    />
                    {game.home_team_label} - {game.home_team?.team_name || 'TBD'}
                  </div>
                </SelectItem>
                <SelectItem value="away">
                  <div className="flex items-center gap-2">
                    <TeamLogo 
                      teamName={game.away_team?.team_name || game.away_team_label}
                      logoUrl={game.away_team?.team_logo}
                      size="sm"
                    />
                    {game.away_team_label} - {game.away_team?.team_name || 'TBD'}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Add Player Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Add Player to {selectedTeamSide === 'home' ? game.home_team_label : game.away_team_label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!teamId ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No team assigned to {selectedTeamSide === 'home' ? 'home' : 'away'} side.</p>
                  <p className="text-sm">Please assign a team first using "Add {selectedTeamSide === 'home' ? 'Home' : 'Away'} Team" button.</p>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a player to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailablePlayers().length === 0 ? (
                        <SelectItem value="no-players" disabled>
                          No available players found for this team
                        </SelectItem>
                      ) : (
                        getAvailablePlayers().map((player) => (
                          <SelectItem key={player.user_id} value={player.user_id}>
                            {getPlayerName(player.user_id)} ({player.user_id.slice(0, 8)})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddPlayer} disabled={!selectedPlayerId || loading}>
                    {loading ? 'Adding...' : 'Add Player'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Roster */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Home Team */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TeamLogo 
                      teamName={game.home_team?.team_name || game.home_team_label}
                      logoUrl={game.home_team?.team_logo}
                      size="md"
                    />
                    <span>{game.home_team_label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${getReadyPlayerCount('home') >= game.minimum_players_required ? 'text-sports-green' : 'text-destructive'}`}>
                      {getReadyPlayerCount('home')}/{game.minimum_players_required} Ready
                    </span>
                  </div>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {game.home_team?.team_name || 'No team assigned'}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {getParticipantsByTeamSide('home').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No players added yet
                  </p>
                ) : (
                  getParticipantsByTeamSide('home').map((participant) => (
                    <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback>
                            {getPlayerName(participant.player_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{getPlayerName(participant.player_id)}</p>
                          {participant.position && (
                            <p className="text-xs text-muted-foreground">{participant.position}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PlayerStatusBadge status={participant.participation_status} size="sm" />
                        <Select
                          value={participant.participation_status}
                          onValueChange={(value) => handleStatusUpdate(participant.id, value as GameParticipant['participation_status'])}
                          disabled={loading}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status.replace('_', ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Away Team */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TeamLogo 
                      teamName={game.away_team?.team_name || game.away_team_label}
                      logoUrl={game.away_team?.team_logo}
                      size="md"
                    />
                    <span>{game.away_team_label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${getReadyPlayerCount('away') >= game.minimum_players_required ? 'text-sports-green' : 'text-destructive'}`}>
                      {getReadyPlayerCount('away')}/{game.minimum_players_required} Ready
                    </span>
                  </div>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {game.away_team?.team_name || 'No team assigned'}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {getParticipantsByTeamSide('away').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No players added yet
                  </p>
                ) : (
                  getParticipantsByTeamSide('away').map((participant) => (
                    <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback>
                            {getPlayerName(participant.player_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{getPlayerName(participant.player_id)}</p>
                          {participant.position && (
                            <p className="text-xs text-muted-foreground">{participant.position}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PlayerStatusBadge status={participant.participation_status} size="sm" />
                        <Select
                          value={participant.participation_status}
                          onValueChange={(value) => handleStatusUpdate(participant.id, value as GameParticipant['participation_status'])}
                          disabled={loading}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status.replace('_', ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Warnings */}
          {(getParticipantsByTeamSide('home').filter(p => p.participation_status === 'ready_to_play').length < game.minimum_players_required ||
            getParticipantsByTeamSide('away').filter(p => p.participation_status === 'ready_to_play').length < game.minimum_players_required) && (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  <p className="font-medium">Warning: Minimum player requirement not met</p>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Teams that don't meet the minimum player requirement ({game.minimum_players_required} players) 
                  by the roster deadline will forfeit the game.
                </p>
              </CardContent>
            </Card>
          )}
    </div>
  );
};